import type { Issue } from '@qcbot/shared';
import { FixDriftError } from './fixErrors';
import { applyFix as applyPowerPointFix, undoFix as undoPowerPointFix, type FixSnapshot as PowerPointSnapshot } from './applyFixPowerPoint';
import { applyWordFix, undoWordFix, type WordFixSnapshot } from './applyFixWord';
import { getHost } from './officeApi';

export { FixDriftError };

/** What `applyFix` returns and `undoFix` consumes — opaque to callers apart from `describePartialFix`. */
export type FixSnapshot = PowerPointSnapshot | WordFixSnapshot;

function isWordSnapshot(snapshot: FixSnapshot): snapshot is WordFixSnapshot {
  return snapshot.kind.startsWith('word');
}

/** Applies one issue's `fixAction` to the live, open document (PowerPoint deck or Word document).
 * Throws `FixDriftError` if what it expects is no longer there, rather than guessing. */
export async function applyFix(issue: Issue): Promise<FixSnapshot> {
  return getHost() === 'word' ? applyWordFix(issue) : applyPowerPointFix(issue);
}

/** Reverses a previously-applied fix using the snapshot `applyFix` returned. */
export async function undoFix(snapshot: FixSnapshot): Promise<void> {
  return isWordSnapshot(snapshot) ? undoWordFix(snapshot) : undoPowerPointFix(snapshot);
}

/** For a multi-edit fix where some edits couldn't be applied: how many went through, and which
 * were skipped (the reviewer has to do those by hand). Undefined for a fix that applied in full. */
export function describePartialFix(snapshot: FixSnapshot): { applied: number; skipped: { slideNumber: number; reason: string }[] } | undefined {
  if (snapshot.kind !== 'composite' && snapshot.kind !== 'wordComposite') return undefined;
  if (snapshot.skipped.length === 0) return undefined;
  return { applied: snapshot.snapshots.length, skipped: snapshot.skipped };
}
